/**
 * Moving between a style group and a cost sheet (JF 2026-10-13): one style must never have two different unsaved
 * versions. Unsaved changes in the group → saved before a cost sheet opens; unsaved changes on the cost sheet → saved
 * before the group opens. Coming back to the group reloads it, so it shows what was saved on the cost sheet.
 */
import { useStore, type View } from "../../store.ts";
import { api } from "../../shared/api.ts";
import { tFor } from "../../shared/i18n.ts";
import { useGroup } from "./groupStore.ts";

/** Save the cost sheet being edited (new → created). */
export async function saveCurrentSheet(): Promise<void> {
  const st = useStore.getState();
  const s = st.sheet;
  let id = st.sheetId;
  try { if (id) await api.updateSheet(id, s); else id = (await api.createSheet(s)).id; }
  catch (e) { if (id && (e as { status?: number }).status === 404) id = (await api.createSheet(s)).id; else throw e; }
  st.setSheet(s, { id }); st.markSaved(s);
}

/** Ask, then save the group's unsaved styles. false = stay. */
export async function groupSavedOrStay(): Promise<boolean> {
  const g = useGroup.getState();
  const n = g.dirtyCount();
  if (!n) return true;
  const t = tFor(useStore.getState().lang);
  if (!confirm(t(`${n} style(s) in the group have unsaved changes. OK = save them and continue; Cancel = stay in the group.`,
    `组合里有 ${n} 款的修改还没保存。确定 = 先全部保存再继续；取消 = 留在组合。`))) return false;
  await g.saveAll();
  return true;
}

/** Go to a view, keeping the group and the cost sheet from holding two different versions of one style. */
export async function goSafe(v: View): Promise<boolean> {
  const st = useStore.getState();
  const g = useGroup.getState();
  const t = tFor(st.lang);
  try {
    if (st.view === "group" && v === "sheet" && !(await groupSavedOrStay())) return false;
    if (v === "group" && st.view !== "group" && g.group && st.isDirty()) {
      if (!confirm(t("This cost sheet has unsaved changes. OK = save it and open the style group; Cancel = stay here.",
        "这张成本表有未保存的修改。确定 = 先保存再打开款式组合；取消 = 留在这里。"))) return false;
      await saveCurrentSheet();
    }
    // back in the group: show what is saved now (e.g. changed on a cost sheet meanwhile)
    if (v === "group" && st.view !== "group" && g.group && !g.dirtyCount()) await g.open(g.group.id);
  } catch (e) { st.toast(e instanceof Error ? e.message : String(e)); return false; }
  st.go(v);
  return true;
}
